import { useEffect, useState } from "react";
import { Plus, Pencil, CalendarHeart } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { api, formatApiError } from "../../lib/api";

const EMPTY = {
  name: "", price_ttc: "", description: "", accroche: "", inclusions: "",
  order: 1, badge: "", cta_label: "Prendre rendez-vous",
  day_j: false, day_j_label: "Aucune présence Art Event le jour J.", active: true,
};

export default function FormulesCoordinationAdminPage() {
  const [offers, setOffers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/coordination-offers").then((r) => setOffers(r.data)).catch(() => setOffers([]));
  useEffect(() => { load(); }, []);

  const openEdit = (o) => {
    setEditing(o);
    setForm({
      name: o.name, price_ttc: o.price_ttc, description: o.description, accroche: o.accroche,
      inclusions: (o.inclusions || []).join("\n"), order: o.order, badge: o.badge,
      cta_label: o.cta_label, day_j: o.day_j, day_j_label: o.day_j_label, active: o.active,
    });
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      name: form.name.trim(), price_ttc: Number(form.price_ttc), description: form.description.trim(),
      accroche: form.accroche.trim(), inclusions: form.inclusions.split("\n").map((s) => s.trim()).filter(Boolean),
      order: Number(form.order), badge: form.badge.trim(), cta_label: form.cta_label.trim() || "Prendre rendez-vous",
      day_j: !!form.day_j, day_j_label: form.day_j_label.trim(), active: !!form.active,
    };
    if (!payload.name || !payload.price_ttc || !payload.description) { toast.error("Nom, prix TTC et description sont obligatoires"); return; }
    try {
      if (editing) await api.patch(`/admin/coordination-offers/${editing.id}`, payload);
      else await api.post("/admin/coordination-offers", payload);
      toast.success(editing ? "Formule mise à jour" : "Formule créée");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-formules-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline-ae">CMS — Coordination</p>
          <h1 className="mt-1 font-heading text-2xl text-deepgreen sm:text-3xl">Formules de coordination</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Nom, prix TTC, description, accroche, prestations, ordre, badge et CTA — modifiables à tout moment, visibles immédiatement sur la page Coordination.
          </p>
        </div>
        <button type="button" onClick={() => { setEditing(null); setForm({ ...EMPTY, order: offers.length + 1 }); setOpen(true); }} className="btn-primary-ae !px-5 !py-2.5 !text-xs" data-testid="offer-create-button">
          <Plus className="h-4 w-4" /> Nouvelle formule
        </button>
      </div>
      <p className="mt-3 rounded-lg bg-sage-light px-4 py-2.5 text-xs text-deepgreen">
        <strong>Règle métier structurée :</strong> la présence Jour J est un champ dédié (jamais déduit du texte commercial). Elle est figée dans chaque dossier/devis au moment de sa création.
      </p>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        {offers.map((o) => (
          <div key={o.id} className={`card-ae flex flex-col p-5 ${o.badge ? "ring-2 ring-gold" : ""} ${!o.active ? "opacity-60" : ""}`} data-testid={`offer-card-${o.key}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-heading text-lg text-deepgreen">{o.name}</p>
                <p className="font-heading text-xl text-gold">{Number(o.price_ttc).toLocaleString("fr-FR")} € TTC</p>
              </div>
              <div className="flex flex-col items-end gap-1.5">
                <StatusBadge status={o.active ? "Active" : "Inactive"} />
                {o.badge && <span className="rounded-full bg-gold px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-deepgreen" data-testid={`offer-badge-${o.key}`}>{o.badge}</span>}
              </div>
            </div>
            <p className="mt-2 line-clamp-3 text-xs text-muted-foreground">{o.description}</p>
            <p className="mt-2 text-[11px] text-muted-foreground">{(o.inclusions || []).length} prestation(s) · ordre {o.order} · {o.day_j ? "Présence Jour J" : "Sans présence Jour J"}</p>
            <button type="button" onClick={() => openEdit(o)} className="btn-secondary-ae mt-4 !px-4 !py-2 !text-[11px]" data-testid={`offer-edit-${o.key}`}>
              <Pencil className="h-3.5 w-3.5" /> Modifier
            </button>
          </div>
        ))}
        {offers.length === 0 && (
          <div className="card-ae col-span-full p-8 text-center" data-testid="offers-empty">
            <CalendarHeart className="mx-auto h-8 w-8 text-sage" />
            <p className="mt-3 text-sm text-muted-foreground">Aucune formule — la page publique affiche alors le contenu intégré par défaut.</p>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-cream sm:max-w-2xl" data-testid="offer-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-deepgreen">{editing ? `Modifier — ${editing.name}` : "Nouvelle formule"}</DialogTitle>
            <DialogDescription>Les modifications sont visibles immédiatement sur la page publique et tracées (AuditLog).</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label-ae" htmlFor="of-name">Nom</label>
              <input id="of-name" className="input-ae" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="offer-form-name" />
            </div>
            <div>
              <label className="label-ae" htmlFor="of-price">Prix TTC (€)</label>
              <input id="of-price" type="number" step="1" min="0" className="input-ae" value={form.price_ttc} onChange={(e) => setForm({ ...form, price_ttc: e.target.value })} data-testid="offer-form-price" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-ae" htmlFor="of-desc">Description (positionnement)</label>
              <textarea id="of-desc" rows={3} className="input-ae" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="offer-form-description" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-ae" htmlFor="of-inc">Prestations incluses (une par ligne)</label>
              <textarea id="of-inc" rows={8} className="input-ae font-mono text-xs" value={form.inclusions} onChange={(e) => setForm({ ...form, inclusions: e.target.value })} data-testid="offer-form-inclusions" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-ae" htmlFor="of-accroche">Accroche (affichée entre guillemets)</label>
              <input id="of-accroche" className="input-ae" value={form.accroche} onChange={(e) => setForm({ ...form, accroche: e.target.value })} data-testid="offer-form-accroche" />
            </div>
            <div>
              <label className="label-ae" htmlFor="of-badge">Badge (ex. « Coup de cœur », vide = aucun)</label>
              <input id="of-badge" className="input-ae" value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} data-testid="offer-form-badge" />
            </div>
            <div>
              <label className="label-ae" htmlFor="of-cta">Libellé du bouton (CTA)</label>
              <input id="of-cta" className="input-ae" value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} data-testid="offer-form-cta" />
            </div>
            <div>
              <label className="label-ae" htmlFor="of-order">Ordre d'affichage</label>
              <input id="of-order" type="number" min="1" className="input-ae" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="offer-form-order" />
            </div>
            <div className="flex flex-col justify-end gap-2">
              <label className="flex items-center gap-2 text-xs font-semibold text-deepgreen">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 accent-[#2C4C3B]" data-testid="offer-form-active" />
                Formule active (visible publiquement)
              </label>
              <label className="flex items-center gap-2 text-xs font-semibold text-deepgreen">
                <input type="checkbox" checked={form.day_j} onChange={(e) => setForm({ ...form, day_j: e.target.checked })} className="h-4 w-4 accent-[#2C4C3B]" data-testid="offer-form-dayj" />
                Présence Art Event le jour J (règle métier)
              </label>
            </div>
            <div className="sm:col-span-2">
              <label className="label-ae" htmlFor="of-dayjlabel">Libellé présence Jour J (affiché publiquement)</label>
              <input id="of-dayjlabel" className="input-ae" value={form.day_j_label} onChange={(e) => setForm({ ...form, day_j_label: e.target.value })} data-testid="offer-form-dayj-label" />
            </div>
          </div>
          <button type="button" onClick={save} className="btn-primary-ae modal-sticky-btn mt-4 w-full" data-testid="offer-form-save">
            {editing ? "Enregistrer les modifications" : "Créer la formule"}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
